import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    FormControl,
    Select,
    MenuItem,
    Slider,
    Switch,
    FormControlLabel,
    Button,
    Alert,
    CircularProgress,
    Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axiosInstance from '../authentication/TokenRefresher';
import Header from '../components/header/Header';
import SideMenu from '../components/navigation/SideMenu';
import AppNavbar from '../components/navigation/AppNavbar';
import { useAuth } from '../authentication/AuthContext';

const StyledCard = styled(Card)(({ theme }) => ({
    backgroundColor: '#ffffff',
    border: '1px solid #bdbdbd',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: theme.spacing(2),
}));

const SettingSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const SettingLabel = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    color: '#333',
}));

const SettingDescription = styled(Typography)(({ theme }) => ({
    fontSize: '0.875rem',
    color: '#666',
    marginBottom: theme.spacing(1),
}));

export default function Preferences() {
    const { accessToken } = useAuth();

    const [preferences, setPreferences] = useState({
        chunks_to_retrieve: 5,
        answer_style: 'concise',
        show_citations: false,
        response_mode: 'generation',
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Load existing preferences + saved API keys
    useEffect(() => {
        const loadPreferences = async () => {
            setLoading(true);
            setError('');
            setSuccess('');

            try {
                const response = await axiosInstance.get('/preferences/', {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    withCredentials: true,
                });

                const data =
                    response?.data?.preferences ?? response?.data ?? {};
                setPreferences((prev) => ({
                    ...prev,
                    ...data,
                }));
            } catch (err) {
                setError('Failed to load preferences. Please try again.');
                console.error('Error loading preferences:', err);
            } finally {
                setLoading(false);
            }
        };

        if (accessToken) {
            loadPreferences();
        }
    }, [accessToken]);

    const handleSavePreferences = async () => {
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const payload = {
                ...preferences,
            };

            await axiosInstance.post('/preferences/', payload);

            setSuccess('Preferences saved successfully!');
        } catch (err) {
            setError('Failed to save preferences. Please try again.');
            console.error('Error saving preferences:', err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <SideMenu />
            <AppNavbar />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    bgcolor: 'background.default',
                }}
            >
                <Header />
                <Box
                    sx={{
                        flex: 1,
                        width: '100%',
                        maxWidth: '100%',
                        px: { xs: 2, sm: 3 },
                        py: { xs: 2, sm: 3 },
                        pb: 5,
                        mt: { xs: 8, md: 0 },
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: 800,
                            margin: '0 auto',
                            width: '100%',
                        }}
                    >
                        {error && (
                            <Alert
                                severity="error"
                                sx={{ mb: 2 }}
                                onClose={() => setError('')}
                            >
                                {error}
                            </Alert>
                        )}

                        {success && (
                            <Alert
                                severity="success"
                                sx={{ mb: 2 }}
                                onClose={() => setSuccess('')}
                            >
                                {success}
                            </Alert>
                        )}

                        {loading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    py: 4,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <StyledCard>
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            sx={{
                                                mb: 3,
                                                fontWeight: 600,
                                            }}
                                        >
                                            RAG Preferences
                                        </Typography>

                                        <SettingSection>
                                            <SettingLabel variant="body1">
                                                Chunks to retrieve:{' '}
                                                {preferences.chunks_to_retrieve}
                                            </SettingLabel>
                                            <SettingDescription>
                                                Number of document chunks to
                                                retrieve per query. Higher
                                                values provide more context but
                                                increase cost and latency.
                                            </SettingDescription>
                                            <Box sx={{ px: 2 }}>
                                                <Slider
                                                    value={
                                                        preferences.chunks_to_retrieve
                                                    }
                                                    onChange={(e, value) =>
                                                        setPreferences(
                                                            (prev) => ({
                                                                ...prev,
                                                                chunks_to_retrieve:
                                                                    value,
                                                            })
                                                        )
                                                    }
                                                    min={1}
                                                    max={20}
                                                    step={1}
                                                    marks={[
                                                        {
                                                            value: 1,
                                                            label: '1',
                                                        },
                                                        {
                                                            value: 5,
                                                            label: '5',
                                                        },
                                                        {
                                                            value: 10,
                                                            label: '10',
                                                        },
                                                        {
                                                            value: 15,
                                                            label: '15',
                                                        },
                                                        {
                                                            value: 20,
                                                            label: '20',
                                                        },
                                                    ]}
                                                    sx={{ mt: 2 }}
                                                />
                                            </Box>
                                        </SettingSection>

                                        <Divider sx={{ my: 2 }} />

                                        <SettingSection>
                                            <SettingLabel variant="body1">
                                                Answer Style
                                            </SettingLabel>
                                            <SettingDescription>
                                                Choose how detailed the AI
                                                responses should be
                                            </SettingDescription>
                                            <FormControl
                                                fullWidth
                                                size="medium"
                                            >
                                                <Select
                                                    value={
                                                        preferences.answer_style
                                                    }
                                                    onChange={(e) =>
                                                        setPreferences(
                                                            (prev) => ({
                                                                ...prev,
                                                                answer_style:
                                                                    e.target
                                                                        .value,
                                                            })
                                                        )
                                                    }
                                                    sx={{ minHeight: 40 }}
                                                >
                                                    <MenuItem value="concise">
                                                        Concise
                                                    </MenuItem>
                                                    <MenuItem value="detailed">
                                                        Detailed
                                                    </MenuItem>
                                                    <MenuItem value="conversational">
                                                        Conversational
                                                    </MenuItem>
                                                    <MenuItem value="technical">
                                                        Technical
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </SettingSection>

                                        <SettingSection>
                                            <SettingLabel variant="body1">
                                                Response Mode
                                            </SettingLabel>
                                            <SettingDescription>
                                                Generation uses the selected
                                                LLM. Retrieval only returns
                                                ranked chunks without calling
                                                the model.
                                            </SettingDescription>
                                            <FormControl
                                                fullWidth
                                                size="medium"
                                            >
                                                <Select
                                                    value={
                                                        preferences.response_mode
                                                    }
                                                    onChange={(e) =>
                                                        setPreferences(
                                                            (prev) => ({
                                                                ...prev,
                                                                response_mode:
                                                                    e.target
                                                                        .value,
                                                            })
                                                        )
                                                    }
                                                    sx={{ minHeight: 40 }}
                                                >
                                                    <MenuItem value="generation">
                                                        Generation
                                                    </MenuItem>
                                                    <MenuItem value="retrieval">
                                                        Retrieval Only
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </SettingSection>

                                        <Divider sx={{ my: 2 }} />

                                        <SettingSection>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={
                                                            preferences.show_citations
                                                        }
                                                        onChange={(e) =>
                                                            setPreferences(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    show_citations:
                                                                        e.target
                                                                            .checked,
                                                                })
                                                            )
                                                        }
                                                        size="medium"
                                                    />
                                                }
                                                label={
                                                    <Box>
                                                        <SettingLabel
                                                            variant="body1"
                                                            sx={{ mb: 0 }}
                                                        >
                                                            Show Citations
                                                        </SettingLabel>
                                                        <SettingDescription>
                                                            Include source
                                                            references with
                                                            answers to show
                                                            where information
                                                            was retrieved from
                                                        </SettingDescription>
                                                    </Box>
                                                }
                                                sx={{
                                                    alignItems: 'flex-start',
                                                    ml: 0,
                                                }}
                                            />
                                        </SettingSection>
                                    </CardContent>
                                </StyledCard>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        mt: 3,
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        onClick={handleSavePreferences}
                                        disabled={saving}
                                        sx={{
                                            minWidth: 160,
                                            height: 40,
                                            fontWeight: 500,
                                        }}
                                    >
                                        {saving ? (
                                            <>
                                                <CircularProgress
                                                    size={16}
                                                    sx={{ mr: 1 }}
                                                />
                                                Saving...
                                            </>
                                        ) : (
                                            'Save Preferences'
                                        )}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
